import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AppRegistrationGet,
  AppRegistrationPut,
} from "../../../../models/AppRegistration"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  clientId: z.string().min(1, "Client ID is required"),
})

const returnURL = `/app-registrations`

export function AppRegistrationDetails(props: {
  readonly appRegistration: AppRegistrationGet
  readonly setAppRegistration: (updatedAppRegistration: AppRegistrationGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { appRegistration, setAppRegistration } = props

  const formMethods = useForm<AppRegistrationPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = formMethods

  useEffect(() => {
    reset({
      name: appRegistration.name,
      clientId: appRegistration.clientId,
    })
  }, [appRegistration, reset])

  const onSubmit = useCallback(
    (values: AppRegistrationPut) =>
      digitalManagerApi
        .put<AppRegistrationGet>(`/appRegistrations/${appRegistration.id}`, {
          clientId: values.clientId,
          name: values.name,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("App registration updated")
            setAppRegistration(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update app registration")
          throw new Error("Cannot update app registration")
        }),
    [appRegistration.id, setAppRegistration]
  )

  const handleNameChange = useCallback(
    (field: ControllerRenderProps<AppRegistrationPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleClientIdChange = useCallback(
    (field: ControllerRenderProps<AppRegistrationPut, "clientId">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<AppRegistrationPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameChange]
  )

  const renderClientIdTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<AppRegistrationPut, "clientId"> }) => (
      <MUITextField
        id="clientId"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleClientIdChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.clientId?.message === "string"
            ? { clientId: { message: errors.clientId.message } }
            : undefined
        }
      />
    ),
    [errors.clientId, handleClientIdChange]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<AppRegistrationPut> = {
    formMethods,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.AR,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{appRegistration.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Client ID",
        displayItem: <div className="title">{appRegistration.clientId}</div>,
        editItem: (
          <Controller
            name="clientId"
            control={control}
            render={renderClientIdTextField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
